import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useApi from 'hooks/useApi';
import meFactory from 'utils/factories/me';
import seasonPassFactory from 'utils/factories/seasonPass';
import usersFactory from 'utils/factories/user';

export const useFriendSeasonPass = (uniqueId?: string | null) => {
  const api = useApi();

  return useQuery({
    queryKey: usersFactory.seasonPass(uniqueId!),
    queryFn: () => api.seasonPassUsers.apiSeasonPassUsersuserUniqueIdGet(uniqueId!),
    select: (v) => v.data,
    enabled: !!uniqueId,
  });
};

export const useSeasonPassUser = ({ uniqueId, isPLayer = false }: { uniqueId?: string | null; isPLayer?: boolean }) => {
  const api = useApi();

  const queryKey = isPLayer ? meFactory.seasonPass() : usersFactory.seasonPass(uniqueId!);

  return useQuery({
    queryKey,
    queryFn: () => api.seasonPassUsers.apiSeasonPassUsersuserUniqueIdGet(uniqueId!),
    select: (v) => v.data,
    enabled: !!uniqueId,
  });
};

export const usePlayerSeasonPass = (uniqueId?: string | null) => {
  const api = useApi();

  return useQuery({
    queryKey: meFactory.seasonPass(),
    queryFn: () => api.seasonPassUsers.apiSeasonPassUsersuserUniqueIdGet(uniqueId!),
    select: (v) => v.data,
    enabled: !!uniqueId,
  });
};

export const usePostSeasonPassFaction = ({
  onSettled,
  userUniqueId,
}: {
  onSettled: () => void;
  userUniqueId?: string | null;
}) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.seasonPassUsers.apiSeasonPassUsersPost,
    onSuccess: (data) => {
      queryClient.setQueryData(meFactory.seasonPass(), (old: any) => {
        return { ...old, data: data.data };
      });
      queryClient.invalidateQueries({ queryKey: meFactory.seasonPass() });

      if (userUniqueId) {
        queryClient.setQueryData(usersFactory.seasonPass(userUniqueId), (old: any) => {
          return { ...old, data: data.data };
        });
        queryClient.invalidateQueries({ queryKey: usersFactory.seasonPass(userUniqueId) });
      }
    },
    onSettled,
  });
};

export const useSeasonsPasses = () => {
  const api = useApi();

  return useQuery({
    queryKey: seasonPassFactory.list(),
    queryFn: () => api.seasonPasses.apiSeasonPassesGetCollection(),
  });
};

export const useSeasonPassesUser = (id?: string, uniqueId?: string) => {
  const api = useApi();

  return useQuery({
    queryKey: seasonPassFactory.seasonPassUser(id!, uniqueId!),
    queryFn: () => api.seasonPasses.apiSeasonPassesIduserUniqueIdGet(id!, uniqueId!),
    enabled: !!(uniqueId && id),
  });
};
